import { drawPolys } from '@common-components/SDKComponent/BoundingPolyOverlay/canvas';

export interface ExportImageOptions {
  quality?: number;
  format?: 'png' | 'jpeg' | 'webp';
  backgroundColor?: string;
}

export interface ExportImageResult {
  blob: Blob;
  width: number;
  height: number;
  filename: string;
}

const MAX_CANVAS_PIXELS = 268435456;
const ESTIMATED_BYTES_PER_PIXEL = 4;
type Redaction2DContext =
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D;

function isRedaction2DContext(
  context: OffscreenCanvasRenderingContext2D | RenderingContext | null
): context is Redaction2DContext {
  return (
    !!context &&
    'drawImage' in context &&
    'fillRect' in context &&
    'fillStyle' in context
  );
}

export function canExportImage(
  width: number,
  height: number
): { canExport: boolean; reason?: string } {
  const pixels = width * height;

  if (pixels > MAX_CANVAS_PIXELS) {
    return {
      canExport: false,
      reason: `Image too large: ${width}x${height} exceeds canvas limit`,
    };
  }

  const estimatedMemory = pixels * ESTIMATED_BYTES_PER_PIXEL;
  const availableMemory =
    (
      performance as Performance & {
        memory?: { jsHeapSizeLimit?: number };
      }
    ).memory?.jsHeapSizeLimit || 0;

  if (availableMemory > 0 && estimatedMemory > availableMemory * 0.5) {
    return {
      canExport: false,
      reason: 'Insufficient memory for export',
    };
  }

  return { canExport: true };
}

export async function exportRedactedImage(
  imageElement: HTMLImageElement | HTMLCanvasElement,
  polys: any[],
  objectTypeEffects: any,
  backgroundBlurLevel: any,
  options: ExportImageOptions = {}
): Promise<ExportImageResult> {
  const {
    quality = 0.95,
    format = 'jpeg',
    backgroundColor = '#000000',
  } = options;

  const width =
    imageElement instanceof HTMLImageElement
      ? imageElement.naturalWidth || imageElement.width
      : imageElement.width;
  const height =
    imageElement instanceof HTMLImageElement
      ? imageElement.naturalHeight || imageElement.height
      : imageElement.height;

  const check = canExportImage(width, height);
  if (!check.canExport) {
    throw new Error(check.reason);
  }

  const useOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  let canvas: OffscreenCanvas | HTMLCanvasElement;
  if (useOffscreenCanvas) {
    canvas = new OffscreenCanvas(width, height);
  } else {
    const htmlCanvas = document.createElement('canvas');
    htmlCanvas.width = width;
    htmlCanvas.height = height;
    canvas = htmlCanvas;
  }

  const ctx = canvas.getContext('2d');
  if (!isRedaction2DContext(ctx)) {
    throw new Error('Failed to create canvas context');
  }

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(imageElement as any, 0, 0, width, height);

  // Drawing a cross-origin image whose response lacks Access-Control-Allow-Origin
  // taints the canvas. The taint only surfaces later as a SecurityError from
  // getImageData / convertToBlob / toBlob, which is opaque to users. Probe with
  // a 1x1 getImageData here so we can throw a message that names the real cause.
  try {
    ctx.getImageData(0, 0, 1, 1);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'SecurityError') {
      throw new Error(
        'Canvas tainted by cross-origin image. The asset URL must respond with Access-Control-Allow-Origin headers so the redacted image can be exported.'
      );
    }
    throw error;
  }

  // Background blur is applied inside drawPolys and blurs the whole canvas
  // independently of the poly list, so drawPolys must run when a background
  // blur is active even if there are no polygons — otherwise a blur-only
  // export would be saved unredacted.
  const hasBackgroundBlur =
    typeof backgroundBlurLevel === 'number' && backgroundBlurLevel > 0;
  if ((polys && polys.length > 0) || hasBackgroundBlur) {
    drawPolys(
      polys,
      [],
      ctx as unknown as CanvasRenderingContext2D,
      'redacted',
      backgroundBlurLevel,
      objectTypeEffects,
      null,
      undefined,
      null,
      imageElement
    );
  }

  const mimeType = `image/${format}`;
  let blob: Blob;
  try {
    blob = useOffscreenCanvas
      ? await (canvas as OffscreenCanvas).convertToBlob({
          type: mimeType,
          quality,
        })
      : await new Promise<Blob>((resolve, reject) => {
          (canvas as HTMLCanvasElement).toBlob(
            (resultBlob) => {
              if (!resultBlob) {
                reject(new Error('Failed to generate blob from canvas'));
                return;
              }
              resolve(resultBlob);
            },
            mimeType,
            quality
          );
        });
  } catch (error) {
    // Backup detection in case drawPolys introduced a tainted source after the
    // initial probe (e.g. an icon or blur source loaded without CORS).
    if (error instanceof DOMException && error.name === 'SecurityError') {
      throw new Error(
        'Canvas tainted while encoding the redacted image. Verify all image sources are served with Access-Control-Allow-Origin headers.'
      );
    }
    throw error;
  }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `redacted-image-${timestamp}.${format}`;

  return {
    blob,
    width,
    height,
    filename,
  };
}

export function downloadImage(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
